import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { getCurriculumSubjectsByGrade } from "../../api/curriculumSubjectsApi";

const ScheduleForm = ({
  isModalVisible,
  setIsModalVisible,
  isEditing,
  formData,
  setFormData,
  handleSubmit,
  loading,
  sessions,
  activeSession,
  grades,
  subjects,
  teachers,
  classrooms,
  terms = [],
}) => {
  // Terms are scoped to the schedule's session; empty list = feature is off.
  const termOptions = [
    { label: "Whole session", value: null },
    ...terms
      .filter((t) => !formData.session_id || t.session_id === formData.session_id)
      .map((t) => ({ label: t.name, value: t.id })),
  ];

  const daysOfWeek = [
    { label: "Monday", value: "mon" },
    { label: "Tuesday", value: "tue" },
    { label: "Wednesday", value: "wed" },
    { label: "Thursday", value: "thu" },
    { label: "Friday", value: "fri" },
    { label: "Saturday", value: "sat" },
    { label: "Sunday", value: "sun" },
  ];

  // Schedule Pattern options. Schedules are day-of-week slots, so every
  // pattern must resolve to concrete weekdays — the server creates one row
  // per day and rejects a payload without any.
  const patternOptions = [
    { label: "Single Day", value: "single" },
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
  ];

  // Predefined time slots (1 hour each)
  const timeOptions = [
    { label: "8:00 AM", value: "08:00:00" },
    { label: "9:00 AM", value: "09:00:00" },
    { label: "10:00 AM", value: "10:00:00" },
    { label: "11:00 AM", value: "11:00:00" },
    { label: "12:00 PM", value: "12:00:00" },
    { label: "1:00 PM", value: "13:00:00" },
    { label: "2:00 PM", value: "14:00:00" },
    { label: "3:00 PM", value: "15:00:00" },
    { label: "4:00 PM", value: "16:00:00" },
    { label: "5:00 PM", value: "17:00:00" },
  ];

  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [pattern, setPattern] = useState("single");
  const [selectedDays, setSelectedDays] = useState([]);
  const [endDate, setEndDate] = useState(null);
  // Subjects taught in the selected grade (from its curriculum). Null = no
  // grade selected yet or curriculum not defined — fall back to all subjects.
  const [gradeSubjects, setGradeSubjects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const gradeId = formData.grade_id;
    if (!gradeId) {
      setGradeSubjects(null);
      return;
    }
    (async () => {
      try {
        const curriculum = await getCurriculumSubjectsByGrade(gradeId);
        const list = (curriculum?.data || curriculum || []).map((cs) => cs.subject).filter(Boolean);
        if (!cancelled) setGradeSubjects(list.length ? list : null);
      } catch (e) {
        if (!cancelled) setGradeSubjects(null);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.grade_id]);

  const subjectOptions = gradeSubjects || subjects;

  const handleGradeChange = (e) => {
    const gradeId = e.value;
    setFormData((prev) => ({
      ...prev,
      grade_id: gradeId,
      // Reset subject when the grade changes — the old pick may not be taught there
      subject_id: prev.grade_id === gradeId ? prev.subject_id : null,
    }));
  };

  useEffect(() => {
    // Set initial time values
    if (formData.start_time) {
      const timeStr = formData.start_time instanceof Date 
        ? formatTimeToString(formData.start_time)
        : formData.start_time;
      setSelectedStartTime(timeStr);
    }
    
    if (formData.end_time) {
      const timeStr = formData.end_time instanceof Date 
        ? formatTimeToString(formData.end_time)
        : formData.end_time;
      setSelectedEndTime(timeStr);
    }

    // Set initial pattern values (older schedules may carry retired
    // monthly/quarterly/session patterns — treat those as single)
    if (formData.pattern) {
      setPattern(["single", "daily", "weekly"].includes(formData.pattern) ? formData.pattern : "single");
    }
    if (formData.selected_days) {
      setSelectedDays(formData.selected_days);
    }
    if (formData.end_date) {
      setEndDate(new Date(formData.end_date));
    }
  }, [formData]);

  const formatTimeToString = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const timeStringToDate = (timeStr) => {
    if (!timeStr) return null;
    const [hh = '00', mm = '00', ss = '00'] = String(timeStr).split(':');
    const d = new Date();
    d.setHours(parseInt(hh, 10));
    d.setMinutes(parseInt(mm, 10));
    d.setSeconds(parseInt(ss, 10));
    d.setMilliseconds(0);
    return d;
  };

  const handleInputChange = (e, name) => {
    const val = (e.target && e.target.value) || e.value;
    setFormData({ ...formData, [name]: val });
  };

  const handleTermChange = (e) => {
    setFormData((prev) => ({ ...prev, term_id: e.value ?? null }));
  };

  const handlePatternChange = (e) => {
    const selectedPattern = e.value;
    setPattern(selectedPattern);
    setFormData(prev => ({ ...prev, pattern: selectedPattern }));

    // Auto set days based on pattern
    if (selectedPattern === "weekly") {
      const defaultDays = ['mon', 'tue', 'wed', 'thu', 'fri'];
      setSelectedDays(defaultDays);
      setFormData(prev => ({ 
        ...prev, 
        selected_days: defaultDays,
        day_of_week: null // Clear single day
      }));
    } else if (selectedPattern === "daily") {
      const allDays = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
      setSelectedDays(allDays);
      setFormData(prev => ({ 
        ...prev, 
        selected_days: allDays,
        day_of_week: null
      }));
    } else if (selectedPattern === "single") {
      setSelectedDays([]);
      // Keep existing day_of_week if editing
      if (!isEditing) {
        setFormData(prev => ({ ...prev, day_of_week: null }));
      }
    }
  };

  const handleDaysChange = (e) => {
    const days = e.value;
    setSelectedDays(days);
    setFormData(prev => ({ ...prev, selected_days: days }));
  };

  const handleEndDateChange = (e) => {
    const date = e.value;
    setEndDate(date);
    setFormData(prev => ({ ...prev, end_date: date }));
  };

  const handleStartTimeChange = (e) => {
    const timeStr = e.value;
    setSelectedStartTime(timeStr);
    
    // Update formData with Date object
    const dateObj = timeStringToDate(timeStr);
    setFormData(prev => ({
      ...prev,
      start_time: dateObj
    }));
    
    // Auto set end time (1 hour later) if not editing or if end time is not set
    if (!isEditing || !formData.end_time) {
      const startHour = parseInt(timeStr.split(':')[0]);
      const endHour = startHour + 1;
      const endTimeStr = `${endHour.toString().padStart(2, '0')}:00:00`;
      setSelectedEndTime(endTimeStr);
      
      setFormData(prev => ({
        ...prev,
        end_time: timeStringToDate(endTimeStr)
      }));
    }
  };

  const handleEndTimeChange = (e) => {
    const timeStr = e.value;
    setSelectedEndTime(timeStr);
    
    // Update formData with Date object
    const dateObj = timeStringToDate(timeStr);
    setFormData(prev => ({
      ...prev,
      end_time: dateObj
    }));
  };

  const getPatternInfo = () => {
    const boundTerm = terms.find((t) => t.id === formData.term_id);
    const duration = boundTerm ? ` — runs during ${boundTerm.name} only` : "";
    return `${getPatternShape()}${duration}`;
  };

  const getPatternShape = () => {
    switch (pattern) {
      case 'single':
        return formData.day_of_week ? 
          `Single schedule on ${daysOfWeek.find(d => d.value === formData.day_of_week)?.label}` : 
          'Single schedule';
      case 'daily':
        return 'Daily schedule (Monday to Sunday)';
      case 'weekly':
        return `Weekly schedule on ${selectedDays.map(d =>
          daysOfWeek.find(day => day.value === d)?.label).join(', ')}`;
      default:
        return '';
    }
  };

  const footer = (
    <div>
      <Button
        label={isEditing ? "Update" : "Create"}
        icon="pi pi-check"
        onClick={handleSubmit}
        loading={loading}
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={isEditing ? "Edit Schedule" : "Create New Schedule"}
      visible={isModalVisible}
      style={{ width: "55vw" }}
      onHide={() => setIsModalVisible(false)}
      footer={footer}
    >
      <div className="p-fluid p-formgrid p-grid">
        {/* First Row */}
        <div className="flex gap-3 mb-4">
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="teacher_id">Teacher *</label>
            <Dropdown
              id="teacher_id"
              value={formData.teacher_id}
              options={teachers}
              onChange={(e) => handleInputChange(e, "teacher_id")}
              optionLabel="displayName"
              optionValue="id"
              placeholder="Select a Teacher"
              filter
              filterBy="displayName"
              filterPlaceholder="Search teacher..."
              itemTemplate={(t) => (
                <div className="flex align-items-center justify-content-between w-full">
                  <span>{t.displayName}</span>
                  <span className="text-xs text-color-secondary ml-2">#{t.id}</span>
                </div>
              )}
              valueTemplate={(t, props) =>
                t ? (
                  <span>{t.displayName} <span className="text-xs text-color-secondary">#{t.id}</span></span>
                ) : (
                  <span className="p-placeholder">{props.placeholder}</span>
                )
              }
              required
            />
          </div>
          
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="classroom_id">Classroom *</label>
            <Dropdown
              id="classroom_id"
              value={formData.classroom_id}
              options={classrooms}
              onChange={(e) => handleInputChange(e, "classroom_id")}
              optionLabel="name"
              optionValue="id"
              placeholder="Select a Classroom"
              required
            />
          </div>
        </div>

        {/* Second Row - Grade drives the Subject list (grade curriculum) */}
        <div className="flex gap-3 mb-4">
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="grade_id">Grade *</label>
            <Dropdown
              id="grade_id"
              value={formData.grade_id}
              options={grades}
              onChange={handleGradeChange}
              optionLabel="name"
              optionValue="id"
              placeholder="Select a Grade"
              required
            />
          </div>

          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="subject_id">Subject *</label>
            <Dropdown
              id="subject_id"
              value={formData.subject_id}
              options={subjectOptions}
              onChange={(e) => handleInputChange(e, "subject_id")}
              optionLabel="name"
              optionValue="id"
              placeholder={formData.grade_id ? "Select a Subject" : "Select a grade first"}
              disabled={!formData.grade_id}
              required
            />
            {formData.grade_id && !gradeSubjects && (
              <small className="text-color-secondary">
                No curriculum defined for this grade — showing all subjects.
              </small>
            )}
          </div>
        </div>

        {/* Third Row - recurrence shape + duration side by side:
            Pattern = which weekdays; Runs during = whole session or one term
            (duration picker only for schools with the terms feature on). */}
        <div className="flex gap-3 mb-4">
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="pattern">Schedule Pattern *</label>
            <Dropdown
              id="pattern"
              value={pattern}
              options={patternOptions}
              onChange={handlePatternChange}
              placeholder="Select Pattern"
              className="w-full"
              required
            />
          </div>

          {terms?.length > 0 && (
            <div className="p-field p-col-12 p-md-6 w-6">
              <label htmlFor="term_id">Runs During</label>
              <Dropdown
                id="term_id"
                value={formData.term_id ?? null}
                options={termOptions}
                onChange={handleTermChange}
                placeholder="Whole session"
                className="w-full"
                showClear
              />
              <small className="text-color-secondary">
                Pick a term to run this class only during that term's available weeks.
              </small>
            </div>
          )}
        </div>

        {/* Pattern Configuration */}
        {pattern === "single" && (
          <div className="flex gap-3 mb-4">
            <div className="p-field p-col-12 p-md-6 w-6">
              <label htmlFor="day_of_week">Day of Week *</label>
              <Dropdown
                id="day_of_week"
                value={formData.day_of_week}
                options={daysOfWeek}
                onChange={(e) => handleInputChange(e, "day_of_week")}
                placeholder="Select a Day"
                required
              />
            </div>
          </div>
        )}

        {(pattern === "weekly" || pattern === "daily") && (
          <>
            <div className="flex gap-3 mb-4">
              <div className="p-field p-col-12 p-md-6 w-6">
                <label htmlFor="selected_days">
                  {pattern === "weekly" ? "Select Days *" : "Days"}
                </label>
                <MultiSelect
                  id="selected_days"
                  value={selectedDays}
                  options={daysOfWeek}
                  onChange={handleDaysChange}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Days"
                  className="w-full"
                  disabled={pattern === "daily"}
                  required={pattern === "weekly"}
                />
              </div>

              <div className="p-field p-col-12 p-md-6 w-6">
                <label htmlFor="end_date">End Date (Optional)</label>
                <Calendar
                  id="end_date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  dateFormat="dd/mm/yy"
                  showIcon
                  placeholder="Select End Date"
                  className="w-full"
                  minDate={new Date()}
                />
              </div>
            </div>
          </>
        )}

        {/* Pattern Info Display */}
        <div className="p-field p-col-12 mb-4">
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="text-sm text-blue-700 font-medium">
              <i className="pi pi-info-circle mr-2"></i>
              {getPatternInfo()}
            </p>
            {pattern !== "single" && (
              <p className="text-xs text-blue-600 mt-1">
                Multiple schedules will be created automatically based on this pattern.
              </p>
            )}
          </div>
        </div>

        {/* Time Selection */}
        <div className="flex gap-3 mb-4">
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="start_time">Start Time *</label>
            <Dropdown
              id="start_time"
              value={selectedStartTime}
              options={timeOptions}
              onChange={handleStartTimeChange}
              placeholder="Select Start Time"
              required
            />
          </div>
          <div className="p-field p-col-12 p-md-6 w-6">
            <label htmlFor="end_time">End Time *</label>
            <Dropdown
              id="end_time"
              value={selectedEndTime}
              options={timeOptions}
              onChange={handleEndTimeChange}
              placeholder="Select End Time"
              required
            />
          </div>
        </div>
        
        {/* Notes */}
        <div className="p-field p-col-12">
          <label htmlFor="notes">Notes</label>
          <InputTextarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange(e, "notes")}
            rows={3}
            placeholder="Additional notes about this schedule..."
          />
        </div>

      </div>
    </Dialog>
  );
};

export default ScheduleForm;